import orderBy from 'lodash/orderBy';

import {getAlphaLabelFromIndex} from 'common/util';
import {FlowsQueryEventEntry} from 'common/widgets/query-entry-section/types';

import {SankeyNode} from './sankey-node';
import {SankeyAnchorType, SankeyNodeType, SankeyNodeTypePriority, SankeyStepType} from './types';
import {SerializedSankeyNode} from './serialized';

export class SankeyStep {
  public nodes: SankeyNode[];
  public step: number;
  public type: SankeyStepType;
  public anchorStepIndex: number;
  public anchorType: SankeyAnchorType;
  public canAddForwardStep: boolean;
  public canAddReverseStep: boolean;
  public anchor: number;
  public section: number;
  public queryStep: FlowsQueryEventEntry;

  constructor(
    nodes: SerializedSankeyNode[],
    step: number,
    canAddForwardStep: boolean,
    canAddReverseStep: boolean,
    stepType: SankeyStepType,
    anchorStepIndex: number,
    anchor: number,
    alignment?: number[],
    queryStep?: FlowsQueryEventEntry,
  ) {
    this.step = step;
    this.canAddForwardStep = canAddForwardStep;
    this.canAddReverseStep = canAddReverseStep;
    this.anchorStepIndex = anchorStepIndex;
    this.anchor = anchor;

    nodes = nodes || [];
    const isFirstNodeAnchor = nodes[0] && nodes[0].type === SankeyNodeType.Anchor;

    this.type = stepType;

    this.anchorType = isFirstNodeAnchor ? nodes[0].anchorType : SankeyAnchorType.Normal;

    this.section = this.type === SankeyStepType.Forward ? anchor + 1 : anchor;

    this.nodes = (nodes || []).map(
      (node) =>
        new SankeyNode(
          {
            ...node,
            step: node.step || step,
          },
          alignment && alignment.length > 0 ? alignment[this.section] : null,
          this.section,
        ),
    );

    this.sortNodes();
    this.fillNodeIndeces();

    this.queryStep = queryStep;
  }

  get isAnchor(): boolean {
    return this.type === SankeyStepType.Anchor;
  }

  get isNormalAnchor(): boolean {
    return this.anchorType === SankeyAnchorType.Normal && this.isAnchor;
  }

  get isForward(): boolean {
    return this.type === SankeyStepType.Forward;
  }

  get isReverse(): boolean {
    return this.type === SankeyStepType.Reverse;
  }

  get weight(): number {
    return this.nodes.reduce((sum, node) => sum + node.totalCount, 0);
  }

  getStepLabel(): string {
    let stepLabel = this.nodes.length && this.nodes[0].eventString;
    if (this.type === SankeyStepType.Anchor && this.queryStep.event) {
      if (this.queryStep.event.renamed) {
        stepLabel = this.queryStep.event.renamed;
      } else if (this.queryStep.event.custom && this.queryStep.event.label) {
        stepLabel = this.queryStep.event.label;
      }
    }
    return stepLabel;
  }

  get getLetterLabel(): string {
    return this.isNormalAnchor ? getAlphaLabelFromIndex(this.anchor) : null;
  }

  private fillNodeIndeces() {
    this.nodes.forEach((node, index) => (node.index = index));
  }

  private sortNodes(): void {
    this.nodes = orderBy(
      this.nodes,
      [
        // "Dropoff" and "Other events" nodes are last
        ({type}) => SankeyNodeTypePriority[type],
        // nodes with greatest weight come first
        `totalCount`,
        // break count ties by sorting event name
        `eventString`,
      ],
      [`desc`, `desc`, `asc`],
    );
  }
}
